<script setup lang="ts">
/**
 * One artifact in the conversation. A single email draft appears in full, right where it was made: the
 * creator reads, edits, sends or deletes it there (a send or delete updates the artifact to Sent / Discarded
 * everywhere it shows). Everything else is a chip that opens in the workspace pane.
 *
 * Phones (`chatBubbles`): the draft is a compact mail card (DraftCard) that opens the DraftSheet instead of the
 * whole inbox composer inline in a 358px chat column. The card is small and in the chat's own chunk: a lazy one
 * rendered nothing until its code arrived, so it popped in late, past the entrance its row was given.
 */
import { computed, defineAsyncComponent } from 'vue'
import type { ArtifactRef } from '../artifacts'
import { useCardMode } from '../cardMode'
import ArtifactChip from './ArtifactChip.vue'
import DraftCard from './DraftCard.vue'

const InlineDraft = defineAsyncComponent(() => import('./workspace/DraftViewer.vue'))

const props = defineProps<{ artifact: ArtifactRef }>()
const { phone: bubbles } = useCardMode()
const isDraft = computed(() => props.artifact.type === 'draft' && !props.artifact.items)
</script>

<template>
    <DraftCard v-if="isDraft && bubbles" :artifact="artifact" class="inline-draft" />
    <InlineDraft v-else-if="isDraft" :artifact="artifact" inline class="inline-draft" data-testid="inline-draft" />
    <ArtifactChip v-else :artifact="artifact" />
</template>

<style scoped>
.inline-draft { grid-column: 1 / -1; min-width: 0; }
</style>
